import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownUp } from 'lucide-react';
import {
  Card,
  EmptyState,
  FilterSelect,
  IconButton,
  Input,
  MemoryCirclesEmpty,
  PageBody,
  Spinner,
  buttonVariants,
  useNow,
} from '@wundo/ui';
import { PageTitle } from '@/components/PageTitle.tsx';
import { OneOnOneCard } from '@/components/team/OneOnOneCard.tsx';
import { TeamIntroBanner } from '@/components/team/TeamIntroBanner.tsx';
import { TeamListHeader } from '@/components/team/TeamListHeader.tsx';
import { TeamSummary } from '@/components/team/TeamSummary.tsx';
import {
  DEFAULT_TEAM_SORT,
  sortColleagues,
  type TeamSort,
} from '@/components/team/sortColleagues.ts';
import { inTeamFilter, type TeamFilter } from '@/components/team/teamFilter.ts';
import { useOneOnOneSupport } from '@/components/team/useOneOnOneSupport.ts';
import { useColleagueRelationship } from '@/components/team/useColleagueRelationship.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { fetchOneOnOneColleagues } from '@/lib/colleagues.ts';
import { fetchConnectedProviders } from '@/lib/integrations.ts';
import { usePublishChatPageOffer } from '@/lib/chatPageOffer.ts';
import { useChatPageNudge } from '@/lib/useChatPageNudge.ts';
import { m } from '@/paraglide/messages.js';

export const Route = createFileRoute('/_app/team/')({
  component: TeamPage,
});

/* Where the drawer beside this page is standing, for the nudge it asks for. */
const NUDGE_PAGE = { page: 'team' } as const;

/**
 * The colleagues the user has 1:1s with, a card each, and for each whether
 * Wundo prepares those 1:1s automatically. The switch is the same person
 * follow as an onboarding pick; the list is the one source for the cards, the
 * filter counts and the card on top. See docs/design/team.md.
 */
export function TeamPage() {
  const nudge = useChatPageNudge(NUDGE_PAGE);
  usePublishChatPageOffer({
    quickActions: [
      { text: m.chat_quick_team_longest_ago(), icon: 'recap' },
      { text: m.chat_quick_team_attention_split(), icon: 'people' },
    ],
    nudge,
  });

  const [filter, setFilter] = useState<TeamFilter>('all');
  /* Narrowed and ordered in the page: the whole list is already here, and it fits in a hand. */
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<TeamSort>(DEFAULT_TEAM_SORT);

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const { data: connected } = useQuery({
    queryKey: ['integrations'],
    queryFn: fetchConnectedProviders,
  });

  /* The card and the counts always describe the page itself, never the hidden ones. */
  const {
    data: colleagues = [],
    isPending,
    isError,
  } = useQuery({
    queryKey: ['oneOnOneColleagues', user?.id, 'active'],
    queryFn: () => fetchOneOnOneColleagues(user!.id, { hidden: false }),
    enabled: Boolean(user?.id),
  });
  const hiddenList = useQuery({
    queryKey: ['oneOnOneColleagues', user?.id, 'hidden'],
    queryFn: () => fetchOneOnOneColleagues(user!.id, { hidden: true }),
    enabled: Boolean(user?.id) && filter === 'hidden',
  });

  const support = useOneOnOneSupport();
  const relationshipChange = useColleagueRelationship();

  const now = useNow();
  const countOf = (option: TeamFilter) =>
    colleagues.filter((colleague) => inTeamFilter(colleague, option, now)).length;
  const wanted = search.trim().toLowerCase();
  const shown = sortColleagues(
    (filter === 'hidden'
      ? (hiddenList.data ?? [])
      : colleagues.filter((colleague) => inTeamFilter(colleague, filter, now))
    ).filter(
      (colleague) =>
        wanted === '' ||
        colleague.label.toLowerCase().includes(wanted) ||
        colleague.emails.some((email) => email.toLowerCase().includes(wanted)),
    ),
    sort,
  );

  const noCalendar = connected !== undefined && !connected.microsoft && !connected.google;

  const listState =
    filter === 'hidden' && hiddenList.isPending
      ? 'loading'
      : filter === 'hidden' && hiddenList.isError
        ? 'failed'
        : shown.length === 0
          ? 'empty'
          : 'cards';

  return (
    <>
      <PageTitle>{m.nav_team()}</PageTitle>
      <PageBody>
        {isPending && <Loading label={m.team_loading()} />}
        {isError && <p className='text-base text-danger'>{m.team_failed()}</p>}

        {!isPending && !isError && colleagues.length === 0 && filter !== 'hidden' && (
          <Card className='flex flex-col items-center py-10'>
            <MemoryCirclesEmpty />
            <EmptyState
              className='pb-0'
              title={m.team_empty_title()}
              description={
                noCalendar ? m.team_empty_no_calendar_body() : m.team_empty_body()
              }
              action={
                noCalendar && (
                  <Link
                    to='/settings/connections'
                    className={buttonVariants({ variant: 'quiet', size: 'sm' })}
                  >
                    {m.empty_connect_calendar()}
                  </Link>
                )
              }
            />
          </Card>
        )}

        {!isPending && !isError && (colleagues.length > 0 || filter === 'hidden') && (
          <>
            {colleagues.length > 0 && (
              <>
                <TeamSummary
                  colleagues={colleagues}
                  onPickGroup={(group) =>
                    setFilter(group === 'memory' ? 'memory' : 'no_memory')
                  }
                />
                <TeamIntroBanner />
              </>
            )}

            <div className='mt-6 flex flex-wrap items-center gap-x-5 gap-y-3'>
              <Input
                type='search'
                className='min-w-60 flex-1'
                aria-label={m.team_search_label()}
                placeholder={m.team_search_placeholder()}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <div className='flex flex-wrap items-center gap-x-5 gap-y-3 max-mobile:gap-x-3'>
                <FilterSelect
                  label={m.team_filter_label()}
                  options={
                    [
                      {
                        value: 'all',
                        label: m.team_filter_all({ count: colleagues.length }),
                      },
                      {
                        value: 'memory',
                        label: m.team_filter_memory({ count: countOf('memory') }),
                      },
                      {
                        value: 'no_memory',
                        label: m.team_filter_no_memory({ count: countOf('no_memory') }),
                      },
                      {
                        value: 'nothing_planned',
                        label: m.team_filter_nothing_planned({
                          count: countOf('nothing_planned'),
                        }),
                      },
                      {
                        value: 'action',
                        label: m.team_filter_action({ count: countOf('action') }),
                      },
                      {
                        value: 'planned',
                        label: m.team_filter_planned({ count: countOf('planned') }),
                      },
                      { value: 'hidden', label: m.team_filter_hidden() },
                    ] as const
                  }
                  value={filter}
                  onValueChange={setFilter}
                />
                <div className='flex items-center gap-1'>
                  <FilterSelect
                    label={m.team_sort_label()}
                    options={
                      [
                        { value: 'next', label: m.team_next_label() },
                        { value: 'name', label: m.team_sort_name() },
                        { value: 'support', label: m.team_support_title() },
                      ] as const
                    }
                    value={sort.key}
                    onValueChange={(key) => setSort({ key, direction: 'asc' })}
                  />
                  <IconButton
                    variant='quiet'
                    size='sm'
                    aria-label={m.team_sort_reverse()}
                    aria-pressed={sort.direction === 'desc'}
                    onClick={() =>
                      setSort({
                        ...sort,
                        direction: sort.direction === 'asc' ? 'desc' : 'asc',
                      })
                    }
                    className='text-ink-muted aria-pressed:text-ink'
                  >
                    <ArrowDownUp className='size-3.5' aria-hidden='true' />
                  </IconButton>
                </div>
              </div>
            </div>
            {(support.isError || relationshipChange.isError) && (
              <p role='alert' className='mt-3 text-sm text-danger'>
                {m.team_support_failed()}
              </p>
            )}

            {listState === 'loading' && (
              <Loading label={m.team_loading()} className='mt-4 px-1 py-2' />
            )}
            {listState === 'failed' && (
              <p className='mt-4 px-1 py-2 text-base text-danger'>
                {m.team_hidden_failed()}
              </p>
            )}
            {listState === 'empty' && (
              <Card tone='quiet' className='mt-4'>
                <EmptyState
                  className='py-4'
                  title={
                    filter === 'hidden' ? m.team_hidden_empty() : m.team_filter_empty()
                  }
                />
              </Card>
            )}
            {listState === 'cards' && (
              <>
                <TeamListHeader />
                <ul aria-label={m.nav_team()} className='mt-2 flex flex-col gap-2.5'>
                  {shown.map((colleague) => (
                    <OneOnOneCard
                      key={colleague.id}
                      colleague={colleague}
                      onSupportChange={(supported) =>
                        support.mutate({ colleague, supported })
                      }
                      onRelationshipChange={(relationship) =>
                        relationshipChange.mutate({ colleague, relationship })
                      }
                    />
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </PageBody>
    </>
  );
}

function Loading({ label, className }: { label: string; className?: string }) {
  return (
    <p className={`flex items-center gap-2 text-base text-ink-muted ${className ?? ''}`}>
      <Spinner />
      {label}
    </p>
  );
}
